import { useTranslation } from '@cobliteam/i18n';
import Popover, { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { useRef, useState } from 'react';

type AIBetaFlagVariant = 'default' | 'light';

interface AIBetaFlagProps {
  variant?: AIBetaFlagVariant;
  className?: string;
}

const HOVER_CLOSE_DELAY_MS = 120;

const AIBetaFlag = ({ variant = 'default', className }: AIBetaFlagProps) => {
  const { t } = useTranslation('ai');
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleOpen = () => {
    cancelCloseTimeout();
    setOpen(true);
  };

  const handleCloseSoon = () => {
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-label={t('beta_flag.tooltip_title')}
          onMouseEnter={handleOpen}
          onMouseLeave={handleCloseSoon}
          onFocus={handleOpen}
          onBlur={handleCloseSoon}
          className={cn(
            'cursor-pointer rounded px-1.5 py-0.5 font-semibold text-[10px] uppercase leading-none outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            variant === 'light' ? 'bg-white/20 text-white' : 'bg-[#FFA931] text-black',
            className
          )}
        >
          Beta
        </button>
      </PopoverTrigger>
      <PopoverContent className='flex flex-col gap-2' style={{ width: 300 }} align='start' showArrow={false} onMouseEnter={handleOpen} onMouseLeave={handleCloseSoon}>
        <div className='flex items-start gap-1.5'>
          <Icon name='info' className='mt-0.5 h-4 w-4 flex-shrink-0 text-gray-10' />
          <Typography variant='description-title'>{t('beta_flag.tooltip_title')}</Typography>
        </div>
        <div className='flex flex-col gap-1'>
          <Typography variant='description-list-default'>{t('beta_flag.tooltip_description')}</Typography>
          <Typography variant='description-list-default' className='text-xs italic'>
            {t('beta_flag.tooltip_notice')}
          </Typography>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AIBetaFlag;
